import {
  infiniteQueryOptions,
  isCancelledError,
  queryOptions,
  type QueryClient,
} from "@tanstack/react-query";
import type { WorkspaceTeamService } from "@/services/workspace-team.service";
import type {
  TTeam,
  TTeamHomeRecordKind,
  TTeamHomeRecordsPage,
  TTeamHomeRecordsSummary,
  TTeamMember,
  TTeamsListQuery,
  TTeamsPage,
} from "@/types/team";
import { getQueryClient, peekQueryClient } from "./client";
import { invalidateScoped } from "./invalidation";
import { FRESHNESS, withinAttempt } from "./policies";
import { scopedKey, scopedRead, scopeIfReady, type QueryScope } from "./scope";

/**
 * The workspace-team reads the caching pilot moves onto Query (NG-2545,
 * contract §2, R14 to R19):
 *
 * - **R14** `teams.bank`: every team the caller may list, walked across all
 *   pages, `[slug, uid, 'teams', 'bank', {include}]`. Reference data for the
 *   sidebar and the pickers. Each `include` mode is its own entry, so a plain
 *   reader is never handed the `include=all` superset (NG-2413).
 * - **R15** `teams.page`: the server-driven teams list, one cursor page at a
 *   time, `[slug, uid, 'teams', 'page', {search, order, mine, root_only, include}]`.
 *   Mutable working data.
 * - **R16** `teams.detail`: one team, `[slug, uid, 'team', id]`. Mutable.
 * - **R17** `teams.members`: one team's roster, `[slug, uid, 'team', id, 'members']`.
 *   Access-sensitive: who is on a team decides what its members can see.
 * - **R18** `teams.children`: a team's direct sub-teams or all descendants,
 *   `[slug, uid, 'team', id, 'children', {mode}]`. Reference.
 * - **R19** `teams.memberTeams`: the teams one member is on, with their role,
 *   `[slug, uid, 'member', mid, 'teams']`. Access-sensitive.
 *
 * R16 sits under `'team'` so a prefix invalidation of `('team', id)` also
 * reaches R17 and R18 beneath it; R14 and R15 sit under `'teams'` so it does
 * not. Every read is a plain idempotent GET, so all declare `"safe-read"` and
 * `runRead` owns their retries.
 *
 * The definitions take the service rather than reaching for the root store, so
 * a store passes `rootStore.workspaceTeamService` and a test passes a fake.
 */

/** Teams by name, the order every team list renders in. */
export function sortTeams(teams: TTeam[] | undefined | null): TTeam[] {
  if (!Array.isArray(teams)) return [];
  return [...teams].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Which teams, and how much of each, the list carries. `all` widens it from the
 * caller's own teams to the whole workspace; `members` embeds each roster.
 */
export type TeamsInclude = "none" | "members" | "all" | "all+members";

/** The include mode for the store's `{includeAll, includeMembers}` options. */
export function teamsIncludeOf(
  options: { includeAll?: boolean; includeMembers?: boolean } = {},
): TeamsInclude {
  if (options.includeAll) return options.includeMembers ? "all+members" : "all";
  return options.includeMembers ? "members" : "none";
}

/** The include modes whose entries embed rosters, so a roster write stales them. */
const TEAMS_INCLUDE_WITH_MEMBERS: readonly TeamsInclude[] = ["members", "all+members"];

/** The store's and the service's `{includeAll, includeMembers}` for an include mode. */
function teamsOptionsOf(include: TeamsInclude) {
  return {
    includeAll: include === "all" || include === "all+members",
    includeMembers: include === "members" || include === "all+members",
  };
}

/** The R14 key. */
export function teamsBankKey(scope: QueryScope, include: TeamsInclude) {
  return scopedKey(scope, "teams", "bank", { include });
}

/**
 * R14: the workspace's teams in one include mode, every page. `"none"` sends no
 * `include`, the same request `WorkspaceTeamsStore.fetchTeams` makes today.
 *
 * A retry restarts the walk from the first page, and the whole walk is bounded
 * by one attempt's timeout (`withinAttempt`), as the project bank's is.
 */
export function teamsBankQuery(
  service: Pick<WorkspaceTeamService, "list">,
  scope: QueryScope,
  include: TeamsInclude,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: teamsBankKey(scope, include),
    queryFn: scopedRead(
      (request) =>
        withinAttempt(request, (bounded) =>
          service.list(scope.tenant, teamsOptionsOf(include), bounded),
        ),
      { retry: "safe-read" },
    ),
  });
}

/**
 * What the teams page asks for, minus the cursor. Every field changes the
 * response, so every field is in the key. The page size is not offered: the
 * service's default is the only one the page uses, and a parameter outside the
 * key may not vary the answer.
 */
export interface TeamsPageParams {
  search?: string;
  order?: TTeamsListQuery["order"];
  mine?: boolean;
  rootOnly?: boolean;
  include?: TeamsInclude;
}

/**
 * The R15 representation. Search is trimmed because the service drops a blank
 * one, so `""` and `"  "` are the same request and share one entry.
 */
function teamsPageRepresentation({
  search,
  order,
  mine = false,
  rootOnly = false,
  include = "none",
}: TeamsPageParams) {
  return { search: search?.trim() ?? "", order: order ?? null, mine, root_only: rootOnly, include };
}

/** The R15 key. */
export function teamsPageKey(scope: QueryScope, params: TeamsPageParams = {}) {
  return scopedKey(scope, "teams", "page", teamsPageRepresentation(params));
}

/**
 * R15: the teams page, one cursor page per fetch. The first page is fetched
 * with no cursor; the next is offered only while the server says there is one
 * and its cursor moved, the same stop rule as `getAllPages`.
 */
export function teamsPageQuery(
  service: Pick<WorkspaceTeamService, "listPage">,
  scope: QueryScope,
  params: TeamsPageParams = {},
) {
  const representation = teamsPageRepresentation(params);
  const query: TTeamsListQuery = {
    ...teamsOptionsOf(representation.include),
    search: representation.search || undefined,
    order: representation.order ?? undefined,
    mine: representation.mine,
    rootOnly: representation.root_only,
  };
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: teamsPageKey(scope, params),
    initialPageParam: null as string | null,
    queryFn: scopedRead<TTeamsPage, string | null>(
      (request, { pageParam }) =>
        service.listPage(scope.tenant, { ...query, cursor: pageParam }, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: TTeamsPage, _all, cursor) =>
      last.next_page_results && last.next_cursor && last.next_cursor !== cursor
        ? last.next_cursor
        : undefined,
  });
}

/** The R16 key. */
export function teamDetailKey(scope: QueryScope, teamId: string) {
  return scopedKey(scope, "team", teamId);
}

/** R16: one team, in `scope`'s workspace. */
export function teamDetailQuery(
  service: Pick<WorkspaceTeamService, "retrieve">,
  scope: QueryScope,
  teamId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: teamDetailKey(scope, teamId),
    queryFn: scopedRead((request) => service.retrieve(scope.tenant, teamId, request), {
      retry: "safe-read",
    }),
  });
}

/** The R17 key. */
export function teamMembersKey(scope: QueryScope, teamId: string) {
  return scopedKey(scope, "team", teamId, "members");
}

const TEAM_ID_SLOT = "\u0000team-id";

/**
 * The team id of an R17 key in any scope, or `null` for any other key. A member
 * write that cannot say which teams it touched marks every cached roster, so it
 * finds them by key. Built from `teamMembersKey` itself, so a change to the key
 * reaches the match: the scope and the team id are free, the parts around them
 * are not.
 */
export function teamIdOfRosterKey(queryKey: readonly unknown[]): string | null {
  const shape = teamMembersKey({ tenant: "", principal: "" }, TEAM_ID_SLOT);
  const slot = shape.indexOf(TEAM_ID_SLOT);
  const matches =
    queryKey.length === shape.length &&
    shape.every((part, i) => i < 2 || i === slot || queryKey[i] === part);
  return matches && typeof queryKey[slot] === "string" ? queryKey[slot] : null;
}

/** R17: one team's roster. Access-sensitive: reused for 60 s, re-read on an access change. */
export function teamMembersQuery(
  service: Pick<WorkspaceTeamService, "listMembers">,
  scope: QueryScope,
  teamId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: teamMembersKey(scope, teamId),
    queryFn: scopedRead((request) => service.listMembers(scope.tenant, teamId, request), {
      retry: "safe-read",
    }),
  });
}

/** Direct sub-teams, or every descendant. */
export type TeamChildrenMode = "direct" | "all";

/** The R18 key. */
export function teamChildrenKey(scope: QueryScope, teamId: string, mode: TeamChildrenMode) {
  return scopedKey(scope, "team", teamId, "children", { mode });
}

/**
 * R18: a team's sub-teams. The two modes are separate entries: the sub-teams
 * tab reads the direct children while the add-members dialog reads every
 * descendant of the same team.
 */
export function teamChildrenQuery(
  service: Pick<WorkspaceTeamService, "listChildren">,
  scope: QueryScope,
  teamId: string,
  mode: TeamChildrenMode,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: teamChildrenKey(scope, teamId, mode),
    queryFn: scopedRead((request) => service.listChildren(scope.tenant, teamId, mode, request), {
      retry: "safe-read",
    }),
  });
}

/** The R19 key. */
export function memberTeamsKey(scope: QueryScope, memberId: string) {
  return scopedKey(scope, "member", memberId, "teams");
}

/** R19: the teams one member is on. Access-sensitive: reused for 60 s, re-read on an access change. */
export function memberTeamsQuery(
  service: Pick<WorkspaceTeamService, "listMemberTeams">,
  scope: QueryScope,
  memberId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: memberTeamsKey(scope, memberId),
    queryFn: scopedRead((request) => service.listMemberTeams(scope.tenant, memberId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * What a confirmed change to who is on which team invalidates (contract §3.2 and
 * §3.3): the R14 entries that embed rosters, R17 for each team whose roster
 * moved, and R19 for each member whose teams moved. The team writers and the
 * member writers (bulk import, removing someone from the workspace) both call
 * this, so the set is named once. R14 entries without rosters, R15, R16 and R18
 * are not touched: membership does not change a team's row.
 */
export async function invalidateTeamRosters(
  client: QueryClient,
  scope: QueryScope,
  { teamIds = [], memberIds = [] }: { teamIds?: readonly string[]; memberIds?: readonly string[] },
): Promise<void> {
  await Promise.all([
    ...TEAMS_INCLUDE_WITH_MEMBERS.map((include) =>
      client.invalidateQueries({ queryKey: teamsBankKey(scope, include), exact: true }),
    ),
    ...teamIds.map((teamId) => invalidateScoped(client, scope, "team", teamId, "members")),
    ...memberIds.map((memberId) => invalidateScoped(client, scope, "member", memberId, "teams")),
  ]);
}

/**
 * The projects linked to one team, every page, `[slug, uid, 'team', id,
 * 'projects']`: the team's Projects tab. Mutable working data. Under the team's
 * detail, so a write to the team (its projects, a move, a delete) reaches it.
 */
export function teamProjectsQuery(
  service: Pick<WorkspaceTeamService, "listTeamProjects">,
  scope: QueryScope,
  teamId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: scopedKey(scope, "team", teamId, "projects"),
    queryFn: scopedRead(() => service.listTeamProjects(scope.tenant, teamId), {
      retry: "safe-read",
    }),
  });
}

/**
 * Who the given teams connect (NG-2967), `[slug, uid, 'teams', 'connected',
 * {teams}]`, the ids sorted so the same roster shares one entry. A permission
 * answer, so access-sensitive; under `teams`, so a team write re-reads it.
 */
export function connectedUsersQuery(
  service: Pick<WorkspaceTeamService, "listConnectedUsers">,
  scope: QueryScope,
  teamIds: readonly string[],
) {
  const teams = [...teamIds].sort();
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "teams", "connected", { teams }),
    queryFn: scopedRead(() => service.listConnectedUsers(scope.tenant, teams), {
      retry: "safe-read",
    }),
  });
}

/**
 * Who a new program delivered by the given teams may be owned by (NG-3164),
 * `[slug, uid, 'teams', 'program-owner-candidates', {teams}]`, the ids sorted so the
 * same roster shares one entry. A permission answer, so access-sensitive; under
 * `teams`, so a team write re-reads it.
 */
export function programOwnerCandidatesQuery(
  service: Pick<WorkspaceTeamService, "listProgramOwnerCandidates">,
  scope: QueryScope,
  teamIds: readonly string[],
  programId?: string,
) {
  const teams = [...teamIds].sort();
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "teams", "program-owner-candidates", {
      teams,
      program: programId ?? null,
    }),
    queryFn: scopedRead(() => service.listProgramOwnerCandidates(scope.tenant, teams, programId), {
      retry: "safe-read",
    }),
  });
}

/** The action a creatable-teams list answers for. */
export type CreatableTeamsAction = "team.create" | "project.create";

/**
 * The teams the caller may create `action` under, `[slug, uid, 'teams',
 * 'creatable', {action}]`: a permission answer in list form, so it is
 * access-sensitive. Under `teams`, so a team write or a `teams:bank` hint
 * re-reads it with the lists.
 */
export function creatableTeamsQuery(
  service: Pick<WorkspaceTeamService, "listCreatable">,
  scope: QueryScope,
  action: CreatableTeamsAction,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "teams", "creatable", { action }),
    queryFn: scopedRead(() => service.listCreatable(scope.tenant, action), {
      retry: "safe-read",
    }),
  });
}

/**
 * The teams the caller may put on a program's Team Access (NG-1916),
 * `[slug, uid, 'teams', 'assignable', {for: 'program'}]`. Narrower than what
 * the caller can see, and access-sensitive, as `creatableTeamsQuery` is.
 */
export function programAssignableTeamsQuery(
  service: Pick<WorkspaceTeamService, "listAssignableForProgram">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "teams", "assignable", { for: "program" }),
    queryFn: scopedRead(() => service.listAssignableForProgram(scope.tenant), {
      retry: "safe-read",
    }),
  });
}

/**
 * The teams the caller may make a project's or program's Home (NG-2969),
 * `[slug, uid, 'teams', 'assignable', {for: 'home'}]`: the teams they are on,
 * narrower than {@link programAssignableTeamsQuery}'s connected set, and
 * access-sensitive like it.
 */
export function homeEligibleTeamsQuery(
  service: Pick<WorkspaceTeamService, "listHomeEligible">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "teams", "assignable", { for: "home" }),
    queryFn: scopedRead(() => service.listHomeEligible(scope.tenant), {
      retry: "safe-read",
    }),
  });
}

/**
 * What the Reassign Home team editor's list asks for (NG-2156), the cursor
 * included: the editor pages back and forth by the server's cursors, so each
 * page is its own entry. The page size is fixed by the editor and every field
 * changes the answer, so every field is in the key.
 */
interface HomeRecordsPageParams {
  kind?: TTeamHomeRecordKind;
  q?: string;
  needsTeam?: boolean;
  perPage: number;
  cursor: string | null;
}

/** The home-records entries of one team, every filter and page: what a picks save re-reads. */
export function homeRecordsPrefix(scope: QueryScope, teamId: string) {
  return scopedKey(scope, "team", teamId, "home-records");
}

/**
 * One page of a root team's live Home records with each one's pick,
 * `[slug, uid, 'team', id, 'home-records', {kind, q, needs_team, per_page, cursor}]`.
 * Mutable working data. Under the team's detail, so a write to the team reaches
 * it. The search is trimmed because the service drops a blank one.
 *
 * @writers `useHomeRecords`' picks save (`components/teams/home-reassignment/
 * use-home-records.ts`) re-reads every page of the team once the PUT answers.
 */
export function homeRecordsPageQuery(
  service: Pick<WorkspaceTeamService, "listHomeRecords">,
  scope: QueryScope,
  teamId: string,
  { kind, q, needsTeam = false, perPage, cursor }: HomeRecordsPageParams,
) {
  const search = q?.trim() ?? "";
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: scopedKey(scope, "team", teamId, "home-records", {
      kind: kind ?? null,
      q: search,
      needs_team: needsTeam,
      per_page: perPage,
      cursor,
    }),
    queryFn: scopedRead<TTeamHomeRecordsPage>(
      (request) =>
        service.listHomeRecords(
          scope.tenant,
          teamId,
          { kind, q: search, needsTeam, perPage, cursor: cursor ?? undefined },
          request,
        ),
      { retry: "safe-read" },
    ),
  });
}

/** A root team's Home records summary entry, `[slug, uid, 'team', id, 'home-records-summary']`. */
export function homeRecordsSummaryKey(scope: QueryScope, teamId: string) {
  return scopedKey(scope, "team", teamId, "home-records-summary");
}

/**
 * The Reassign Home page's summary of a root team (NG-2156): its counts, the
 * team options and any reassignment job running on it. Mutable working data,
 * outside {@link homeRecordsPrefix}, since a picks save answers with it. A 403
 * (not an admin: the page falls back to the delete preflight) and a 404 (a job
 * deleted the team) are answers the page expects, so the cache logs them as
 * warnings.
 *
 * @writers `useReassignment` (`components/teams/home-reassignment/
 * use-reassignment.ts`) reads it afresh on open, while it follows a job and
 * after a run, and puts each picks save's answer here.
 */
export function homeRecordsSummaryQuery(
  service: Pick<WorkspaceTeamService, "homeRecordsSummary">,
  scope: QueryScope,
  teamId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    meta: { ...FRESHNESS.mutable.meta, expectedStatuses: [403, 404] },
    queryKey: homeRecordsSummaryKey(scope, teamId),
    queryFn: scopedRead<TTeamHomeRecordsSummary>(
      (request) => service.homeRecordsSummary(scope.tenant, teamId, request),
      { retry: "safe-read" },
    ),
  });
}

/** Whether `queryKey` is a cached team list, R14 or R15, in any scope and mode. */
export function isTeamsListKey(queryKey: readonly unknown[]): boolean {
  return queryKey[2] === "teams" && (queryKey[3] === "bank" || queryKey[3] === "page");
}

/** Whether `queryKey` is a cached team detail (R16), in any scope. */
function isTeamDetailKey(queryKey: readonly unknown[]): boolean {
  return queryKey.length === 4 && queryKey[2] === "team";
}

/** Every row of an R15 page set, in order, once each. */
export function rowsOfTeamsPages(pages: readonly TTeamsPage[]): TTeam[] {
  const seen = new Set<string>();
  const rows: TTeam[] = [];
  for (const page of pages) {
    for (const team of page.results) {
      if (seen.has(team.id)) continue;
      seen.add(team.id);
      rows.push(team);
    }
  }
  return rows;
}

/**
 * A team this tab has cached, from its detail (R16) or any team list (R14,
 * R15): the lookup a writer makes for the row it is about to change. Not an
 * observable read: a component observes the team through `useTeam`.
 */
export function findCachedTeam(teamId: string): TTeam | undefined {
  const client = peekQueryClient();
  if (!client) return undefined;
  const cache = client.getQueryCache();
  for (const query of cache.findAll({ predicate: ({ queryKey }) => isTeamDetailKey(queryKey) })) {
    if (query.queryKey[3] === teamId && query.state.data) return query.state.data as TTeam;
  }
  for (const query of cache.findAll({ predicate: ({ queryKey }) => isTeamsListKey(queryKey) })) {
    const data = query.state.data as TTeam[] | { pages: TTeamsPage[] } | undefined;
    const rows = Array.isArray(data) ? data : data ? rowsOfTeamsPages(data.pages) : [];
    const team = rows.find((row) => row.id === teamId);
    if (team) return team;
  }
  return undefined;
}

/**
 * Read through the cache, again when a team write cancels the read: a write
 * cancels the reads in flight so an answer sent for before it cannot land over
 * it, and the reader asks again, after the write. Bounded, so a write loop
 * cannot hold a loader for good.
 */
async function readAfterWrites<T>(read: () => Promise<T>, attempts = 3): Promise<T> {
  try {
    return await read();
  } catch (error) {
    if (attempts > 1 && isCancelledError(error)) return readAfterWrites(read, attempts - 1);
    throw error;
  }
}

/**
 * The workspace's teams in one include mode (R14), for a reader outside React:
 * a route loader warming the page, or a writer that needs the list. A fresh
 * entry answers with no request and concurrent callers share one. On a cold
 * load, before the session has resolved, it asks the service directly,
 * uncached, so the loader still gets the list.
 */
export function readTeams(
  service: Pick<WorkspaceTeamService, "list">,
  slug: string,
  include: TeamsInclude = "none",
): Promise<TTeam[]> {
  const scope = scopeIfReady(slug, "team list");
  if (!scope) return service.list(slug, teamsOptionsOf(include));
  return readAfterWrites(() =>
    getQueryClient().fetchQuery(teamsBankQuery(service, scope, include)),
  );
}

/** One team (R16) for a reader outside React, as `readTeams` reads the list. */
export function readTeam(
  service: Pick<WorkspaceTeamService, "retrieve">,
  slug: string,
  teamId: string,
): Promise<TTeam> {
  const scope = scopeIfReady(slug, "team");
  if (!scope) return service.retrieve(slug, teamId);
  return readAfterWrites(() =>
    getQueryClient().fetchQuery(teamDetailQuery(service, scope, teamId)),
  );
}

/** One team's roster (R17) for a reader outside React, as `readTeams` reads the list. */
export function readTeamRoster(
  service: Pick<WorkspaceTeamService, "listMembers">,
  slug: string,
  teamId: string,
): Promise<TTeamMember[]> {
  const scope = scopeIfReady(slug, "team roster");
  if (!scope) return service.listMembers(slug, teamId);
  return readAfterWrites(() =>
    getQueryClient().fetchQuery(teamMembersQuery(service, scope, teamId)),
  );
}
